import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "./_utils";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      className={cn(
        "fixed inset-0 z-50 bg-[rgba(var(--scrim-rgb),0.32)] backdrop-blur-sm",
        className
      )}
      {...props}
    />
  );
}

export function DialogContent({
  className,
  children,
  hideClose = false,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  /**
   * Suppress the built-in corner close. For dialogs that mount their own
   * chrome (a fixed header bar with its own DialogClose) — the dialog must
   * still be closable by what replaces it.
   */
  hideClose?: boolean;
}) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        className={cn(
          "fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto " +
            "-translate-x-1/2 -translate-y-1/2 " +
            // Glass: a dialog is staged work floating over the field.
            "rounded-[var(--radius-pane)] bg-[var(--glass-bg)] border border-[var(--glass-border)] " +
            "[-webkit-backdrop-filter:blur(var(--glass-blur))_saturate(1.15)] " +
            "[backdrop-filter:blur(var(--glass-blur))_saturate(1.15)] " +
            "p-xl shadow-[var(--shadow-overlay)]",
          className
        )}
        {...props}
      >
        {children}
        {hideClose ? null : (
          <DialogPrimitive.Close
            className="absolute right-md top-md rounded-sm opacity-70 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:opacity-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

export function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={cn("text-lg font-semibold text-text-primary", className)}
      {...props}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      className={cn("text-sm text-text-secondary", className)}
      {...props}
    />
  );
}

/**
 * Modal dialog built on Radix Dialog (focus trap + Escape-to-close for free).
 *
 * @example
 * <Dialog>
 *   <DialogTrigger asChild><Button>Open</Button></DialogTrigger>
 *   <DialogContent>
 *     <DialogTitle>Confirm</DialogTitle>
 *     <DialogDescription>Are you sure?</DialogDescription>
 *   </DialogContent>
 * </Dialog>
 */
